import {useMemo} from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {useDashboardReport} from '../../../hooks/useDashboardReport';
import {ChartOrMessage} from '../../common';

const SiteBarTooltip = ({active, payload, label}) => {
  if (active && payload && payload.length) {
    return (
      <div className='custom-tooltip'>
        <p className='tooltip-label'>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{color: entry.color}}>
            {entry.dataKey}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const SiteBarChart = ({uid, enabled}) => {
  // Calculate 30-day date range
  const {startDate, endDate} = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);

    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatLocalDate(start),
      endDate: formatLocalDate(end),
    };
  }, []);

  const {loading, error, recordingsData} = useDashboardReport({
    uid,
    startDate,
    endDate,
    enabled,
  });

  const siteBarData = useMemo(() => {
    if (!recordingsData || recordingsData.length === 0) {
      return [];
    }

    const siteData = {};

    recordingsData.forEach((recording) => {
      const siteName = recording.company_name || 'Unknown Site';

      if (!siteData[siteName]) {
        siteData[siteName] = {
          site: siteName,
          Checkins: 0,
          Impairments: 0,
        };
      }

      siteData[siteName].Checkins += 1;

      if (recording.result === '1' || recording.result === 1) {
        siteData[siteName].Impairments += 1;
      }
    });

    return Object.values(siteData).sort((a, b) => a.site.localeCompare(b.site));
  }, [recordingsData]);

  if (loading) {
    return (
      <div className='site-bar-section'>
        <div className='chart-container'>
          <div className='loading'>Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='site-bar-section'>
        <div className='chart-container'>
          <div className='error'>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className='site-bar-section'>
      <div className='chart-container'>
        <div className='text-center'>
          <h3 style={{marginBottom: '0px'}}>
            CHECK INS AND IMPAIRMENT ALERTS BY SITE
          </h3>
          <p style={{marginTop: '5px'}}>Last 30 Days</p>
        </div>

        <div className='chart'>
          <ChartOrMessage data={siteBarData}>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart
                data={siteBarData}
                margin={{top: 20, right: 30, left: 20, bottom: 5}}
              >
                <CartesianGrid vertical={false} strokeDasharray='3 3' />
                <XAxis dataKey='site' />
                <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                <Tooltip content={<SiteBarTooltip />} />
                <Legend />
                <Bar
                  dataKey='Checkins'
                  fill='#2e7d32'
                  name='Check-ins'
                  minPointSize={1}
                />
                <Bar
                  dataKey='Impairments'
                  fill='#d32f2f'
                  name='Impairment Alerts'
                  minPointSize={1}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartOrMessage>
        </div>
      </div>
    </div>
  );
};

export default SiteBarChart;
